import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, Server } from 'lucide-react';
import type { 
  StakeBalanceResponse,
  DelegationHistoryResponse
} from '../types';
import { formatBalance, truncateAddress } from '../utils/chartHelpers';
import { taostats } from '../api/taoStats';
import { subnetNames } from '../utils/subnetNames';
import { SubnetChart } from './SubnetChart';

interface SubnetSectionProps {
  stakeBalanceData: StakeBalanceResponse['data'];
  delegationHistory: DelegationHistoryResponse['data'];
  expandedSubnet: number | null;
  setExpandedSubnet: (netuid: number | null) => void;
  getDelegationsForSubnet: (netuid: number) => Promise<DelegationHistoryResponse['data']>;
}

interface GroupedSubnets {
  [hotkey: string]: {
    subnets: StakeBalanceResponse['data'];
    name: string;
  };
}

interface CostBasisInfo {
  totalTaoSpent: number;
  totalTaoReceived: number;
  totalAlphaReceived: number;
  totalAlphaSpent: number;
  pnl: number;
  pnlPercentage: number;
  currentTaoValue: number;
}

const convertPlanckToTao = (planckBalance: string): number => {
  try {
    // First convert the string to a number and round it to ensure we have an integer
    const roundedValue = Math.round(Number(planckBalance));
    // Then convert to BigInt and back to number, dividing by 1e9
    return Number(BigInt(roundedValue)) / 1e9;
  } catch (error) {
    console.error('Error converting planck to tao:', error);
    return 0;
  }
};

export function SubnetSection({
  stakeBalanceData,
  delegationHistory,
  expandedSubnet,
  setExpandedSubnet,
  getDelegationsForSubnet,
}: SubnetSectionProps) {
  const [groupedSubnets, setGroupedSubnets] = useState<GroupedSubnets>({});
  const [selectedHotkey, setSelectedHotkey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastZeroBalances, setLastZeroBalances] = useState<Record<number, number>>({});
  const [isSubnetSectionActive, setIsSubnetSectionActive] = useState(false);
  const [costBasisData, setCostBasisData] = useState<Record<number, CostBasisInfo>>({});
  const [validatorNames, setValidatorNames] = useState<Record<string, string>>({});
  const validatorFetchQueue = useRef<string[]>([]);
  const isFetchingValidator = useRef(false);

  // Group subnets by hotkey
  useEffect(() => {
    const grouped: GroupedSubnets = {};
    
    for (const subnet of stakeBalanceData.filter(item => item.netuid !== 0)) {
      const hotkey = subnet.hotkey?.ss58;
      if (!hotkey) continue;
      
      if (!grouped[hotkey]) {
        grouped[hotkey] = {
          subnets: [],
          name: validatorNames[hotkey] || truncateAddress(hotkey)
        };
      }
      grouped[hotkey].subnets.push(subnet);
    }

    setGroupedSubnets(grouped);
    setLoading(false);

    if (!selectedHotkey && Object.keys(grouped).length > 0) {
      setSelectedHotkey(Object.keys(grouped)[0]);
    }
    
    // Queue up validator names to fetch
    Object.keys(grouped).forEach(hotkey => {
      if (!validatorNames[hotkey] && !validatorFetchQueue.current.includes(hotkey)) {
        validatorFetchQueue.current.push(hotkey);
      }
    });
    
    // Start fetching validator names if not already fetching
    if (!isFetchingValidator.current && validatorFetchQueue.current.length > 0) {
      fetchNextValidatorName();
    }
  }, [stakeBalanceData, validatorNames]);

  // Function to fetch validator names one at a time to avoid rate limiting
  const fetchNextValidatorName = async () => {
    if (validatorFetchQueue.current.length === 0) {
      isFetchingValidator.current = false;
      return;
    }
    
    isFetchingValidator.current = true;
    const hotkey = validatorFetchQueue.current.shift();
    
    if (!hotkey) {
      isFetchingValidator.current = false;
      return;
    }
    
    try {
      const response = await taostats.getValidator({ hotkey });
      if (response.data[0]?.name) {
        setValidatorNames(prev => ({
          ...prev,
          [hotkey]: response.data[0].name
        }));
      }
    } catch (err) {
      console.error('Failed to fetch validator name:', err);
    } finally {
      // Wait a bit before fetching the next one to avoid rate limiting
      setTimeout(() => {
        fetchNextValidatorName();
      }, 1000);
    }
  };

  // Calculate last zero balance points when subnet section is active
  useEffect(() => {
    if (!isSubnetSectionActive) return;

    const fetchZeroBalances = async () => {
      // Skip if we already have data
      if (Object.keys(lastZeroBalances).length > 0) return;
      
      const zeroBalances: Record<number, number> = {};
      const processedSubnets = new Set<number>();
      
      // Process each subnet to find the first delegation
      for (const subnet of stakeBalanceData) {
        if (!subnet.netuid || subnet.netuid === 0 || processedSubnets.has(subnet.netuid)) continue;
        
        processedSubnets.add(subnet.netuid);
        
        try {
          // Get delegations for this subnet
          const subnetDelegations = await getDelegationsForSubnet(subnet.netuid);
          
          if (Array.isArray(subnetDelegations) && subnetDelegations.length > 0) {
            // Sort delegations by block number in ascending order
            const sortedDelegations = [...subnetDelegations].sort((a, b) => 
              a.block_number - b.block_number
            );
            
            // Find the first delegation
            const firstDelegation = sortedDelegations.find(d => d.action === 'DELEGATE');
            if (firstDelegation) {
              zeroBalances[subnet.netuid] = firstDelegation.block_number;
            }
          }
        } catch (err) {
          console.error(`Failed to process delegations for subnet ${subnet.netuid}:`, err);
        }
      }
      
      setLastZeroBalances(zeroBalances);
    };
    
    fetchZeroBalances();
  }, [isSubnetSectionActive, stakeBalanceData, getDelegationsForSubnet, lastZeroBalances]);

  // Handle cost basis calculation for a subnet
  const handleCostBasisCalculated = (netuid: number, costBasis: CostBasisInfo) => {
    setCostBasisData(prev => ({
      ...prev,
      [netuid]: costBasis
    }));
  };

  // Memoize total PnL calculations
  const totalPnLData = useMemo(() => {
    const costBasisValues = Object.values(costBasisData);
    if (costBasisValues.length === 0) return null;
    
    const totalPnL = costBasisValues.reduce((sum, data) => sum + data.pnl, 0);
    const totalInvested = costBasisValues.reduce((sum, data) => 
      sum + (data.totalTaoSpent - data.totalTaoReceived), 0
    );
    const totalPnLPercentage = totalInvested > 0 ? (totalPnL / totalInvested) * 100 : 0;
    
    return {
      totalPnL,
      totalInvested,
      totalPnLPercentage
    };
  }, [costBasisData]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-10 bg-black/20 rounded-lg w-64"></div>
        <div className="h-32 bg-black/20 rounded-lg"></div>
      </div>
    );
  }

  const currentHotkey = selectedHotkey || Object.keys(groupedSubnets)[0];
  const currentSubnets = groupedSubnets[currentHotkey]?.subnets || [];
  const totalTaoValue = currentSubnets.reduce((sum, subnet) => 
    sum + convertPlanckToTao(subnet.balance_as_tao), 0
  );
  const totalAlphaValue = currentSubnets.reduce((sum, subnet) => 
    sum + convertPlanckToTao(subnet.balance), 0
  );

  return (
    <div className="space-y-6" onMouseEnter={() => setIsSubnetSectionActive(true)}>
      <div className="flex flex-col space-y-4">
        <div className="relative">
          <select
            value={currentHotkey}
            onChange={(e) => setSelectedHotkey(e.target.value)}
            className="w-full md:w-64 bg-black/20 backdrop-blur-sm rounded-lg p-3 pr-10 border border-white/10 text-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-600"
          >
            {Object.entries(groupedSubnets).map(([hotkey, { name }]) => (
              <option key={hotkey} value={hotkey} className="bg-gray-900">
                {name}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
        </div>

        <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 md:p-6 border border-white/5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Server className="w-5 h-5 text-red-600" />
              <div>
                <h3 className="text-lg md:text-2xl font-semibold">{groupedSubnets[currentHotkey]?.name}</h3>
                <p className="text-sm font-mono text-gray-400">{truncateAddress(currentHotkey)}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-red-400">{totalTaoValue.toFixed(4)} τ</p>
              <p className="text-sm text-gray-400">{totalAlphaValue.toFixed(4)} α</p>
              {totalPnLData && (
                <p className={`text-sm font-medium ${totalPnLData.totalPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  PnL: {totalPnLData.totalPnL.toFixed(2)} τ ({totalPnLData.totalPnLPercentage.toFixed(2)}%)
                </p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {currentSubnets
              .sort((a, b) => b.netuid - a.netuid)
              .map((subnet) => {
                const currentAlphaBalance = convertPlanckToTao(subnet.balance);
                const currentTaoBalance = convertPlanckToTao(subnet.balance_as_tao);
                const tokenValuePerAlpha = currentAlphaBalance > 0 
                  ? currentTaoBalance / currentAlphaBalance 
                  : 0;

                const subnetName = subnetNames[subnet.netuid] || 'Unknown';
                const displayName = `SN ${subnet.netuid}: ${subnetName}`;
                
                // Get PnL data for this subnet
                const subnetPnL = costBasisData[subnet.netuid];

                return (
                  <div key={subnet.netuid} className="bg-black/20 backdrop-blur-sm rounded-lg border border-white/5">
                    <div className="p-4">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h4 className="text-base md:text-lg font-semibold">
                            {displayName}
                          </h4>
                          <p className="text-sm text-gray-400 mt-1">
                            {tokenValuePerAlpha.toFixed(4)} τ per α
                          </p>
                          {subnetPnL && (
                            <p className={`text-sm font-medium mt-1 ${subnetPnL.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              PnL: {subnetPnL.pnl.toFixed(2)} τ ({subnetPnL.pnlPercentage.toFixed(2)}%)
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="mb-4">
                        <SubnetChart
                          coldkey={subnet.coldkey?.ss58}
                          hotkey={subnet.hotkey?.ss58}
                          netuid={subnet.netuid.toString()}
                          subnetName={displayName}
                          block_start={lastZeroBalances[subnet.netuid]}
                          onCostBasisCalculated={(costBasis) => handleCostBasisCalculated(subnet.netuid, costBasis)}
                        />
                      </div>

                      <div className="border-t border-white/10 pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <p className="text-sm text-gray-400">Alpha Position</p>
                          <div className="flex items-center space-x-2">
                            <span className="text-base font-bold text-white">
                              {formatBalance(subnet.balance)} α
                            </span>
                          </div>
                        </div>

                        <div className="space-y-1">
                          <p className="text-sm text-gray-400">Token Value</p>
                          <div className="flex flex-col">
                            <span className="text-base font-bold text-white">
                              {formatBalance(subnet.balance_as_tao)} τ
                            </span>
                            {subnetPnL && (
                              <div className="flex items-center space-x-2 mt-1">
                                <span className="text-xs text-gray-400">Cost Basis:</span>
                                <span className="text-xs font-medium">
                                  {(subnetPnL.totalTaoSpent - subnetPnL.totalTaoReceived).toFixed(2)} τ
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}